import { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
    Table, Tag, Button, Modal, Input, Space, Typography,
    Avatar, Tooltip, Badge, Card, Spin, Empty, message
} from "antd";
import {
    CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
    UserOutlined, TeamOutlined, ClockCircleOutlined,
    CheckOutlined, CloseOutlined, EyeOutlined
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Title, Text } = Typography;
const { TextArea } = Input;

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_MAP = {
    PENDING: { color: "gold", icon: <ClockCircleOutlined />, label: "Pending" },
    APPROVED: { color: "green", icon: <CheckCircleOutlined />, label: "Approved" },
    REJECTED: { color: "red", icon: <CloseCircleOutlined />, label: "Rejected" },
};

// ─── Employee mini-card ───────────────────────────────────────────────────────
function EmpCard({ name, code, dept, designation }) {
    const initials = (name || code || "?")
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <Space size={10} align="center">
            <Avatar
                size={36}
                style={{
                    background: "linear-gradient(135deg, #0e497b, #1a6fb5)",
                    fontWeight: 700,
                    fontSize: 13,
                    flexShrink: 0,
                }}
            >
                {initials}
            </Avatar>
            <div style={{ lineHeight: 1.35 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1e3a5f" }}>
                    {name || code}
                </div>
                <div style={{ fontSize: 11, color: "#8fa3bc" }}>
                    {[code, designation, dept].filter(Boolean).join(" · ")}
                </div>
            </div>
        </Space>
    );
}

// ─── Action Modal ─────────────────────────────────────────────────────────────
function ActionModal({ open, action, record, onClose, onSubmit, loading }) {
    const [reason, setReason] = useState("");

    useEffect(() => {
        if (open) setReason("");
    }, [open]);

    const isApprove = action === "APPROVE";
    const title = isApprove ? "Approve Transfer Request" : "Reject Transfer Request";
    const icon = isApprove
        ? <CheckCircleOutlined style={{ color: "#15803d", fontSize: 22 }} />
        : <CloseCircleOutlined style={{ color: "#dc2626", fontSize: 22 }} />;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={480}
            centered
            title={
                <Space>
                    {icon}
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#1e3a5f" }}>{title}</span>
                </Space>
            }
        >
            {record && (
                <div style={{ padding: "8px 0 16px" }}>
                    {/* Transfer summary */}
                    <div style={{
                        background: "#f8fafd",
                        border: "1.5px solid #dde3ed",
                        borderRadius: 12,
                        padding: "14px 16px",
                        marginBottom: 20,
                    }}>
                        <div style={{ marginBottom: 10 }}>
                            <Text type="secondary" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                Outgoing HOD
                            </Text>
                            <div style={{ marginTop: 6 }}>
                                <EmpCard
                                    name={record.outgoing_hod_name}
                                    code={record.outgoing_hod_code}
                                    dept={record.department_name}
                                    designation={record.outgoing_hod_designation}
                                />
                            </div>
                        </div>
                        <div style={{ borderTop: "1px solid #dde3ed", paddingTop: 10 }}>
                            <Text type="secondary" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                Incoming HOD
                            </Text>
                            <div style={{ marginTop: 6 }}>
                                <EmpCard
                                    name={record.incoming_hod_name}
                                    code={record.incoming_hod_code}
                                    dept={record.department_name}
                                    designation={record.incoming_hod_designation}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Reason */}
                    <div style={{ marginBottom: 20 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", display: "block", marginBottom: 8 }}>
                            Reason <span style={{ color: "#dc2626" }}>*</span>
                        </label>
                        <TextArea
                            rows={3}
                            placeholder={isApprove ? "Reason for approval…" : "Reason for rejection…"}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            style={{ borderRadius: 10, fontSize: 13 }}
                        />
                    </div>

                    {/* Buttons */}
                    <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                        <Button onClick={onClose} disabled={loading} style={{ borderRadius: 8 }}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            danger={!isApprove}
                            loading={loading}
                            disabled={!reason.trim()}
                            icon={isApprove ? <CheckOutlined /> : <CloseOutlined />}
                            style={{
                                borderRadius: 8,
                                background: isApprove ? "#2563eb" : undefined,
                                borderColor: isApprove ? "#2563eb" : undefined,
                            }}
                            onClick={() => onSubmit(reason)}
                        >
                            {isApprove ? "Approve" : "Reject"}
                        </Button>
                    </Space>
                </div>
            )}
        </Modal>
    );
}

// ─── Subordinates modal ───────────────────────────────────────────────────────
function SubordinatesModal({ open, record, onClose }) {
    const subs = record?.transferred_employees || [];
    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={<Button onClick={onClose}>Close</Button>}
            title={
                <Space>
                    <TeamOutlined style={{ color: "#2563eb" }} />
                    <span style={{ fontWeight: 700, color: "#1e3a5f" }}>
                        Transferred Employees ({subs.length})
                    </span>
                </Space>
            }
            width={440}
            centered
        >
            {subs.length === 0 ? (
                <Empty description="No subordinates listed" />
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
                    {subs.map((emp, i) => (
                        <div
                            key={i}
                            style={{
                                background: "#f8fafd",
                                border: "1px solid #dde3ed",
                                borderRadius: 10,
                                padding: "10px 14px",
                            }}
                        >
                            <EmpCard
                                name={emp.name || emp.full_name}
                                code={emp.emp_code}
                                dept={emp.department_name}
                                designation={emp.designation_name}
                            />
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HodChange() {
    const { authData, backUrl } = useContext(AuthContext);
    const headers = { Authorization: `Bearer ${authData?.access}` };

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionModal, setActionModal] = useState({ open: false, action: null, record: null });
    const [subsModal, setSubsModal] = useState({ open: false, record: null });
    const [submitting, setSubmitting] = useState(false);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/transfer/list/`, { headers });
            const data = res.data;
            setRequests(Array.isArray(data) ? data : data.results || []);
        } catch {
            message.error("Failed to load transfer requests.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRequests(); }, []);

    const openAction = (record, action) =>
        setActionModal({ open: true, action, record });

    const closeAction = () =>
        setActionModal({ open: false, action: null, record: null });

    const handleSubmit = async (reason) => {
        const { record, action } = actionModal;
        setSubmitting(true);
        try {
            await axios.post(
                `${backUrl}/api/emp/hod-replacement/approve/${record.id}/`,
                { action, reason },
                { headers }
            );
            message.success(
                action === "APPROVE"
                    ? "Request approved successfully!"
                    : "Request rejected successfully."
            );
            closeAction();
            fetchRequests();
        } catch (err) {
            message.error(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Action failed. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    // ── Table columns ──────────────────────────────────────────────────────────
    const columns = [
        {
            title: "Request ID",
            dataIndex: "id",
            key: "id",
            width: 100,
            render: (id) => (
                <Text style={{ fontWeight: 700, color: "#2563eb", fontFamily: "monospace" }}>
                    #{id}
                </Text>
            ),
        },
        {
            title: "Outgoing HOD",
            key: "outgoing",
            render: (_, r) => (
                <EmpCard
                    name={r.outgoing_hod_name}
                    code={r.outgoing_hod_code}
                    dept={r.department_name}
                    designation={r.outgoing_hod_designation}
                />
            ),
        },
        {
            title: "Incoming HOD",
            key: "incoming",
            render: (_, r) => (
                <EmpCard
                    name={r.incoming_hod_name}
                    code={r.incoming_hod_code}
                    dept={r.department_name}
                    designation={r.incoming_hod_designation}
                />
            ),
        },
        {
            title: "Employees",
            key: "employees",
            width: 110,
            align: "center",
            render: (_, r) => {
                const count = r.transferred_employees?.length ?? r.employee_count ?? 0;
                return (
                    <Tooltip title="View transferred employees">
                        <Button
                            type="text"
                            icon={<TeamOutlined style={{ color: "#2563eb" }} />}
                            onClick={() => setSubsModal({ open: true, record: r })}
                            style={{ borderRadius: 8 }}
                        >
                            <Badge
                                count={count}
                                showZero
                                style={{ background: count > 0 ? "#2563eb" : "#c8d5e3" }}
                            />
                        </Button>
                    </Tooltip>
                );
            },
        },
        {
            title: "Requested On",
            dataIndex: "created_at",
            key: "created_at",
            width: 130,
            render: (val) => val
                ? <Text style={{ fontSize: 12, color: "#5a82a6" }}>
                    {new Date(val).toLocaleDateString("en-IN", {
                        day: "2-digit", month: "short", year: "numeric"
                    })}
                </Text>
                : "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => {
                const cfg = STATUS_MAP[status] || STATUS_MAP.PENDING;
                return (
                    <Tag
                        icon={cfg.icon}
                        color={cfg.color}
                        style={{ fontWeight: 600, borderRadius: 6, padding: "2px 10px" }}
                    >
                        {cfg.label}
                    </Tag>
                );
            },
            filters: Object.entries(STATUS_MAP).map(([k, v]) => ({ text: v.label, value: k })),
            onFilter: (val, r) => r.status === val,
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            fixed: "right",
            render: (_, r) => {
                if (r.status !== "PENDING") {
                    return (
                        <Tooltip title="View details">
                            <Button
                                type="text"
                                icon={<EyeOutlined />}
                                style={{ color: "#8fa3bc" }}
                                onClick={() => setSubsModal({ open: true, record: r })}
                            />
                        </Tooltip>
                    );
                }
                return (
                    <Space size={6}>
                        <Tooltip title="Approve">
                            <Button
                                type="primary"
                                size="small"
                                icon={<CheckOutlined />}
                                style={{
                                    borderRadius: 7,
                                    background: "#16a34a",
                                    borderColor: "#16a34a",
                                    fontWeight: 600,
                                }}
                                onClick={() => openAction(r, "APPROVE")}
                            >
                                Approve
                            </Button>
                        </Tooltip>
                        <Tooltip title="Reject">
                            <Button
                                danger
                                size="small"
                                icon={<CloseOutlined />}
                                style={{ borderRadius: 7, fontWeight: 600 }}
                                onClick={() => openAction(r, "REJECT")}
                            >
                                Reject
                            </Button>
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    // ── Stats ──────────────────────────────────────────────────────────────────
    const pending = requests.filter((r) => r.status === "PENDING").length;
    const approved = requests.filter((r) => r.status === "APPROVED").length;
    const rejected = requests.filter((r) => r.status === "REJECTED").length;

    return (
        <div style={{
            minHeight: "100vh",
            background: "#f0f4f8",
            padding: "32px 24px",
            fontFamily: "'DM Sans', sans-serif",
        }}>
            {/* Header */}
            <div style={{ marginBottom: 28 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <Title level={3} style={{ margin: 0, color: "#0e2a4a", fontWeight: 800, letterSpacing: "-0.5px" }}>
                            HOD Transfer Requests
                        </Title>
                        <Text style={{ color: "#5a82a6", fontSize: 13 }}>
                            Review and manage head-of-department replacement requests
                        </Text>
                    </div>
                    <Button
                        icon={<SyncOutlined spin={loading} />}
                        onClick={fetchRequests}
                        style={{ borderRadius: 9, fontWeight: 600, borderColor: "#c8d5e3", color: "#5a82a6" }}
                    >
                        Refresh
                    </Button>
                </div>

                {/* Stats row */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 14,
                    marginTop: 20,
                    maxWidth: 560,
                }}>
                    {[
                        { label: "Pending", value: pending, color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
                        { label: "Approved", value: approved, color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
                        { label: "Rejected", value: rejected, color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
                    ].map((s) => (
                        <div key={s.label} style={{
                            background: s.bg,
                            border: `1.5px solid ${s.border}`,
                            borderRadius: 12,
                            padding: "12px 16px",
                            display: "flex",
                            flexDirection: "column",
                        }}>
                            <Text style={{ fontSize: 11, fontWeight: 700, color: s.color, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                                {s.label}
                            </Text>
                            <Text style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1.2, marginTop: 2 }}>
                                {s.value}
                            </Text>
                        </div>
                    ))}
                </div>
            </div>

            {/* Table */}
            <Card
                style={{
                    borderRadius: 16,
                    border: "1.5px solid #dde3ed",
                    boxShadow: "0 4px 24px rgba(14,73,123,0.07)",
                    overflow: "hidden",
                }}
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    dataSource={requests}
                    columns={columns}
                    rowKey="id"
                    loading={{ spinning: loading, indicator: <Spin size="large" /> }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} requests`,
                        style: { padding: "12px 20px" },
                    }}
                    scroll={{ x: 900 }}
                    locale={{ emptyText: <Empty description="No transfer requests found" style={{ padding: "40px 0" }} /> }}
                    rowClassName={(r) =>
                        r.status === "PENDING" ? "row-pending" : ""
                    }
                    style={{ fontSize: 13 }}
                />
            </Card>

            {/* Action Modal */}
            <ActionModal
                open={actionModal.open}
                action={actionModal.action}
                record={actionModal.record}
                onClose={closeAction}
                onSubmit={handleSubmit}
                loading={submitting}
            />

            {/* Subordinates Modal */}
            <SubordinatesModal
                open={subsModal.open}
                record={subsModal.record}
                onClose={() => setSubsModal({ open: false, record: null })}
            />

            <style>{`
                .row-pending {
                    background: #fffdf0 !important;
                }
                .row-pending:hover > td {
                    background: #fff9e6 !important;
                }
                .ant-table-thead > tr > th {
                    background: #f8fafd !important;
                    color: #5a82a6 !important;
                    font-weight: 700 !important;
                    font-size: 11px !important;
                    letter-spacing: 0.07em !important;
                    text-transform: uppercase !important;
                    border-bottom: 1.5px solid #dde3ed !important;
                }
                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f0f4f8 !important;
                    padding: 14px 16px !important;
                }
                .ant-table-tbody > tr:last-child > td {
                    border-bottom: none !important;
                }
            `}</style>
        </div>
    );
}